import { CREATED_AT, RULE_SET_ID, SOURCE_REFS, makeRuleTrace } from "./sourceAuthority.js";
export const ZERO_POINTS = {
    ui: 0,
    di: 0,
    ao: 0,
    do: 0,
    relay: 0
};
export const loadProfiles = [
    {
        id: "load-profile-control-temperature-input",
        ruleSetId: RULE_SET_ID,
        key: "control_temperature_input",
        label: "Dedicated control-zone temperature input",
        pointDemand: { ...ZERO_POINTS, ui: 1 },
        minQuantity: 1,
        notes: [
            "Every control zone needs a dedicated control temperature input.",
            "Do not reuse an EUFH cutoff/limit sensor as the control temperature input."
        ],
        sourceRefs: [SOURCE_REFS.methodologyDecisions]
    },
    {
        id: "load-profile-wufh-composite-zone",
        ruleSetId: RULE_SET_ID,
        key: "wufh_composite_zone",
        label: "Wet UFH composite control zone",
        pointDemand: { ...ZERO_POINTS, ui: 2, do: 1 },
        minQuantity: 0,
        notes: [
            "Composite point demand = one control temperature UI, one active wet UFH floor/limit UI, and one actuator enable DO.",
            "WUFH actuators are enable-only and never consume AO.",
            "Spare/redundant UFH sensors are not terminated and do not consume controller IO."
        ],
        sourceRefs: [SOURCE_REFS.baselineRules, SOURCE_REFS.methodologyDecisions]
    },
    {
        id: "load-profile-eufh-load",
        ruleSetId: RULE_SET_ID,
        key: "eufh_load",
        label: "Electric UFH load output",
        pointDemand: { ...ZERO_POINTS, do: 1 },
        minQuantity: 0,
        notes: [
            "EUFH load profile is load-only: UI 0, AO 0, DO 1.",
            "The EUFH cutoff/limit sensor is not controller UI.",
            "Use control_temperature_input separately for the zone's room temperature input."
        ],
        sourceRefs: [SOURCE_REFS.baselineRules, SOURCE_REFS.methodologyDecisions]
    },
    {
        id: "load-profile-heat-only-zone",
        ruleSetId: RULE_SET_ID,
        key: "heat_only_zone",
        label: "Standalone heat-only control zone",
        pointDemand: { ...ZERO_POINTS, ui: 1, do: 1 },
        minQuantity: 0,
        notes: [
            "Use a dedicated control temperature input and an enable output.",
            "Single standalone heat-only overflow prefers an additional 710R."
        ],
        sourceRefs: [SOURCE_REFS.baselineRules, SOURCE_REFS.methodologyDecisions]
    },
    {
        id: "load-profile-fcu-heat-cool-base",
        ruleSetId: RULE_SET_ID,
        key: "fcu_heat_cool_base",
        label: "FCU heat/cool base control profile",
        pointDemand: { ...ZERO_POINTS, ui: 1, di: 1, ao: 2, do: 1, relay: 1 },
        minQuantity: 0,
        notes: [
            "Initial base profile keeps FCU mode differences requirement-driven until Rich confirms mode-specific IO deltas.",
            "Includes control temperature input, fan feedback allowance, actuator/fan analogue outputs, fan enable, and source enable allowance."
        ],
        sourceRefs: [SOURCE_REFS.baselineRules, SOURCE_REFS.methodologyDecisions]
    },
    {
        id: "load-profile-spare-ufh-sensor",
        ruleSetId: RULE_SET_ID,
        key: "spare_ufh_sensor_not_terminated",
        label: "Spare UFH sensor, not terminated",
        pointDemand: ZERO_POINTS,
        minQuantity: 0,
        notes: [
            "BOM/supply item only unless explicitly marked active and terminated.",
            "Does not consume controller UI."
        ],
        sourceRefs: [SOURCE_REFS.baselineRules, SOURCE_REFS.methodologyDecisions]
    },
    {
        id: "load-profile-towel-rail-load-relay",
        ruleSetId: RULE_SET_ID,
        key: "towel_rail_load_relay_output",
        label: "Towel rail central load-relay output",
        pointDemand: { ...ZERO_POINTS, do: 1 },
        minQuantity: 0,
        notes: [
            "Towel rail count comes from takeoff hard count.",
            "Towel rails are central relay loads, not controller zones."
        ],
        sourceRefs: [SOURCE_REFS.baselineRules, SOURCE_REFS.methodologyDecisions]
    },
    {
        id: "load-profile-netmaster-zero-io",
        ruleSetId: RULE_SET_ID,
        key: "netmaster_zero_io",
        label: "Netmaster zero hard IO",
        pointDemand: ZERO_POINTS,
        minQuantity: 0,
        notes: [
            "Netmaster provides supervisory functions and mappings only.",
            "Physical IO must terminate on controllers or IO modules."
        ],
        sourceRefs: [SOURCE_REFS.baselineRules, SOURCE_REFS.methodologyDecisions]
    }
];
export function getLoadProfile(key) {
    const profile = loadProfiles.find((candidate) => candidate.key === key);
    if (!profile) {
        throw new Error(`Unknown load profile: ${key}`);
    }
    return profile;
}
export function addPointVectors(...vectors) {
    return vectors.reduce((total, vector) => ({
        ui: total.ui + vector.ui,
        di: total.di + vector.di,
        ao: total.ao + vector.ao,
        do: total.do + vector.do,
        relay: total.relay + vector.relay
    }), ZERO_POINTS);
}
export function getControlZonePointDemand(kind) {
    if (kind === "wufh") {
        const profile = getLoadProfile("wufh_composite_zone");
        return {
            kind,
            pointDemand: profile.pointDemand,
            componentProfileKeys: [profile.key],
            traces: [
                makeRuleTrace("trace-wufh-composite-zone-points", "rule-load-wufh-composite-zone", "WUFH composite zone uses UI 2, AO 0, DO 1 with no AO demand.")
            ]
        };
    }
    if (kind === "eufh") {
        const loadProfile = getLoadProfile("eufh_load");
        const sensorProfile = getLoadProfile("control_temperature_input");
        return {
            kind,
            pointDemand: addPointVectors(loadProfile.pointDemand, sensorProfile.pointDemand),
            componentProfileKeys: [loadProfile.key, sensorProfile.key],
            traces: [
                makeRuleTrace("trace-eufh-load-points", "rule-load-eufh-load-only", "EUFH load contributes UI 0 and DO 1; the separate control sensor contributes one UI.")
            ]
        };
    }
    if (kind === "heat_only") {
        const profile = getLoadProfile("heat_only_zone");
        return {
            kind,
            pointDemand: profile.pointDemand,
            componentProfileKeys: [profile.key],
            traces: [
                makeRuleTrace("trace-heat-only-zone-points", "rule-controller-single-heat-only-710", "Heat-only zone uses a dedicated temperature input and enable output.")
            ]
        };
    }
    const profile = getLoadProfile("fcu_heat_cool_base");
    return {
        kind,
        pointDemand: profile.pointDemand,
        componentProfileKeys: [profile.key],
        traces: [
            makeRuleTrace("trace-fcu-heat-cool-base-points", "rule-controller-710r-two-zone", "FCU base profile is requirement-driven and pinned to 710R primary-zone use.")
        ]
    };
}
export function calculateTerminatedSensorIoDemand(input) {
    const terminatedUi = input.activeControlTemperatureSensors + input.activeWetUfhLimitSensors;
    const notTerminatedSensorCount = input.spareOrRedundantSensors + input.eufhLimitSensors;
    return {
        pointDemand: { ...ZERO_POINTS, ui: terminatedUi },
        notTerminatedSensorCount,
        traces: [
            makeRuleTrace(`trace-sensor-io-${CREATED_AT}`, "rule-sensor-spares-not-terminated", "Only active terminated control and wet UFH limit sensors consume UI; spare and EUFH limit sensors do not.")
        ]
    };
}
//# sourceMappingURL=loadProfiles.js.map